// Sanatan Cinematics — 4K tags for the Janmashtami film (3dfab909).
// A DUPLICATE of PersonTag/LocationTag scaled 3x for 3840x2160 @ 30 fps.
// The 1280x704 @24 originals are untouched; every other composition still uses those.
//   NAME  -> bottom LEFT   (PersonTag convention)
//   PLACE -> bottom RIGHT  (LocationTag convention)
import { useCurrentFrame, useVideoConfig, interpolate, AbsoluteFill } from "remotion";
import { loadFont } from "@remotion/google-fonts/Inter";
import { loadFont as loadDev } from "@remotion/google-fonts/NotoSansDevanagari";

const { fontFamily } = loadFont("normal", { weights: ["600", "700"], subsets: ["latin"] });
const { fontFamily: devFont } = loadDev("normal", { weights: ["400"], subsets: ["devanagari"] });

// 1280 -> 3840 is exactly 3x. Every size below is the original x3.
export const SC_W = 3840, SC_H = 2160, SC_FPS = 30;
const K = 3;
// Yash, 3 Sep: type up 25% at 4K. Applies to the TEXT only — the insets and the
// bottom offsets stay on K so the tags sit in the same place on the frame.
const T = K * 1.25;
// Vertical gap when two names stack on the left. The Devanagari line is the tallest
// glyph box (15*T = 56.25 px) and lineHeight is 1.2, so the text itself needs ~68 px;
// 92 clears it with a little air, without the old 144 px hole. BOTTOM tag stays at 30*K.
export const SC_SLOT = 92;

export type SCTagData = { name: string; name_hindi?: string; sub?: string };

type P = { data: SCTagData; durationInFrames: number; startFrame?: number; bottom?: number };

/** NAME — bottom LEFT. */
export const SCPersonTag: React.FC<P> = ({ data, durationInFrames, startFrame = 0, bottom = 30 * K }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = frame - startFrame;
  if (f < 0 || f >= durationInFrames) return null;
  const alpha = interpolate(f,
    [0, Math.round(0.5 * fps), durationInFrames - Math.round(0.5 * fps), durationInFrames],
    [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <div style={{ position: "absolute", bottom, left: 40 * K, opacity: alpha }}>
        <div style={{ color: "#fff", fontWeight: 700, lineHeight: 1.2, textTransform: "uppercase",
                      display: "flex", gap: 8 * T, alignItems: "baseline" }}>
          <span style={{ fontFamily, fontSize: 13 * T }}>{data.name}</span>
          {data.name_hindi && <>
            <span style={{ fontFamily, opacity: 0.5, fontSize: 13 * T }}>|</span>
            <span style={{ fontFamily: devFont, fontWeight: 400, fontSize: 15 * T }}>{data.name_hindi}</span>
          </>}
        </div>
        {data.sub && (
          <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 * T, fontWeight: 400, fontFamily,
                        marginTop: 2 * K }}>{data.sub}</div>
        )}
      </div>
    </AbsoluteFill>
  );
};

/** PLACE — bottom RIGHT. */
export const SCLocationTag: React.FC<P> = ({ data, durationInFrames, startFrame = 0, bottom = 30 * K }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = frame - startFrame;
  if (f < 0 || f >= durationInFrames) return null;
  const alpha = interpolate(f,
    [0, Math.round(0.3 * fps), durationInFrames - Math.round(0.5 * fps), durationInFrames],
    [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ opacity: alpha, pointerEvents: "none" }}>
      <div style={{ position: "absolute", bottom, right: 40 * K, textAlign: "right" }}>
        <div style={{ color: "#fff", fontWeight: 700, lineHeight: 1.2, textTransform: "uppercase",
                      display: "flex", gap: 8 * T, alignItems: "baseline", justifyContent: "flex-end" }}>
          <span style={{ fontFamily, fontSize: 13 * T }}>{data.name}</span>
          {data.name_hindi && <>
            <span style={{ fontFamily, opacity: 0.5, fontSize: 13 * T }}>|</span>
            <span style={{ fontFamily: devFont, fontWeight: 400, fontSize: 15 * T }}>{data.name_hindi}</span>
          </>}
        </div>
        {data.sub && (
          <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 * T, fontWeight: 400, fontFamily,
                        marginTop: 2 * K }}>{data.sub}</div>
        )}
      </div>
    </AbsoluteFill>
  );
};

const BG = "linear-gradient(135deg,#2a4a3a 0%,#1a2a3a 100%)";
const HOLD = Math.round(2.6 * SC_FPS);   // 78 frames — the hold used in the overlay map

/** Preview: a place on the right and a name on the left, exactly as the film uses them. */
export const SCTagsPreview: React.FC = () => (
  <AbsoluteFill style={{ background: BG }}>
    <SCLocationTag data={{ name: "MATHURA", name_hindi: "मथुरा" }} durationInFrames={HOLD} startFrame={0} />
    <SCPersonTag   data={{ name: "PUTANA",  name_hindi: "पूतना" }} durationInFrames={HOLD} startFrame={Math.round(1.2 * SC_FPS)} />
  </AbsoluteFill>
);

/** Preview: two names stacked on the left, the way #9b and #85 need them. */
export const SCTwoNamesPreview: React.FC = () => (
  <AbsoluteFill style={{ background: BG }}>
    <SCPersonTag data={{ name: "DEVAKI",   name_hindi: "देवकी"  }} durationInFrames={HOLD} startFrame={0}  bottom={30 * K + SC_SLOT} />
    <SCPersonTag data={{ name: "VASUDEVA", name_hindi: "वसुदेव" }} durationInFrames={HOLD} startFrame={36} bottom={30 * K} />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
